import React, { useState, useEffect } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import MailIcon from '@mui/icons-material/Mail';
import { Link } from "react-router-dom";
import "./header.css";
import { jwtDecode } from "jwt-decode";
import { HEADER_ICON_COLOR, HEADING_COLOR } from "../../../constants/style";
import { WEB_BASE_URL } from '../../../constants/index';
import Swal from 'sweetalert2';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StoreIcon from '@mui/icons-material/Store';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import dayjs from 'dayjs';

const now = dayjs();

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  variants: [
    {
      props: ({ open }) => open,
      style: {
        transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: 0,
      },
    },
  ],
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        width: `calc(100% - ${drawerWidth}px)`,
        marginLeft: `${drawerWidth}px`,
        transition: theme.transitions.create(['margin', 'width'], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
  background: "#23bda89e",
  zIndex: 1000,
  color: "black"

}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
  background: "#23bda89e",
  color: "black"
}));

// Role Set 
const rawRole = localStorage.getItem('role');
const role = rawRole ? rawRole.trim().toUpperCase() : null;
// console.log("Normalized Role:", role);

export default function PersistentDrawerLeft({ children }: any) {
  const theme = useTheme();
  const [open, setOpen] = React.useState(true);
  const token: string = localStorage.getItem("token") ? "" + localStorage.getItem("token") + "" : "";
  const decoded: { id: number; role: string; } = jwtDecode(token);
  const location = useLocation();

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const [now, setNow] = useState(dayjs());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(dayjs());
    }, 1000); // update every second

    return () => clearInterval(timer); // cleanup when component unmounts
  }, []);

  const handleDrawerClose = () => {
    setOpen(false);
  };
  let menuList = ['Dashboard', 'Designation', 'Users', 'Attendance Cycle', 'Reports', 'Help', 'Logout'];

  // const logout = () => {
  //   localStorage.removeItem("token");
  //   window.location.href = `${WEB_BASE_URL}login`;
  // }
  useEffect(() => {
    if (location.pathname.includes('reports') || location.pathname.includes('admin')) {
      handleDrawerClose();
    }
  }, [location.pathname]);

  const logout = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you really want to log out?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    }).then((result) => {
      if (result.isConfirmed) {
        // If the user confirmed, proceed with logout
        localStorage.removeItem('token');
        localStorage.removeItem('fullName');
        localStorage.removeItem('role');
        window.location.href = `${WEB_BASE_URL}login`;

        // Optional: show a success message after logout
        Swal.fire({
          title: 'Logged Out',
          text: 'You have been logged out successfully!',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        // Optional: Show a message if the user cancels the logout
        Swal.fire({
          title: 'Cancelled',
          text: 'You are still logged in.',
          icon: 'info',
          timer: 1500,
          showConfirmButton: false,
        });
      }
    });
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={[
                { mr: 2 },
                open && { display: 'none' },
              ]}
            >
              <MenuIcon />
            </IconButton>

            <Typography variant="h6" noWrap component="div">
              Devminds Software
              {localStorage.getItem("fullName") ? " - " + localStorage.getItem("fullName") : ""}
            </Typography>
          </div>
          &nbsp;
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            Date: {now.format('DD-MMMM-YYYY')} &nbsp; | &nbsp; Time: {now.format('hh:mm:ss A')}
          </Typography>
        </Toolbar>
      </AppBar>

      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            background: '#23bda89e',
            border: 'none',
            color: "black"
          },

        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader >
          <div style={{
            paddingLeft: "10px",
            display: "flex",
            width: "100%",
            justifyContent: "space-between"
          }}>
            <img src="/Logodev.png"
              style={{
                width: "138px",
                height: "32px",

              }} />
            <IconButton onClick={handleDrawerClose} size="small" >
              {theme.direction === 'ltr' ? <ChevronLeftIcon style={{ color: "black" }} /> : <ChevronRightIcon style={{ color: "black" }} />}
            </IconButton>
          </div>

        </DrawerHeader>
        <Divider />

        {/* <List>
          {menuList.map((text, index) => (
            <ListItem key={text} disablePadding>
              {text === 'Logout' ? (
                <ListItemButton onClick={logout}>
                  <ListItemIcon>
                    <ExitToAppIcon style={{ color: HEADER_ICON_COLOR }} />
                  </ListItemIcon>
                  <ListItemText primary={text} />
                </ListItemButton>
              ) : (
                <Link
                  to={
                    text === 'Dashboard'
                      ? '/admin'
                      : text === 'Attendance Cycle'
                        ? '/admin/users/cycles'
                        : text === 'Quotation'
                          ? '/admin/quotation/create'
                          : text === 'Designation'
                            ? '/admin/user_types'
                            : text === 'Users'
                              ? '/admin/users'
                              : text === 'Reports'
                                ? '/admin/reports'
                              : text === 'Help'
                                ? '/admin/help'
                                : text === 'Purchase Order'
                                  ? '/admin/purchase_order_list'
                                  : '/admin'
                  }
                  className="linktag"
                >
                  <ListItemButton>
                    <ListItemIcon>

                      {text === 'Attendance Cycle' && <StoreIcon style={{ color: HEADER_ICON_COLOR }} />}
                      {text === 'Quotation' && <DescriptionIcon style={{ color: HEADER_ICON_COLOR }} />}
                      {text === 'Designation' && <CorporateFareIcon style={{ color: HEADER_ICON_COLOR }} />}
                      {text === 'Users' && <GroupIcon style={{ color: HEADER_ICON_COLOR }} />}
                      {text === 'Reports' && <ListAltIcon style={{ color: HEADER_ICON_COLOR }} />}
                      {text === 'Help' && <ListAltIcon style={{ color: HEADER_ICON_COLOR }} />}
                      {text === 'Purchase Order' && <ListAltIcon style={{ color: HEADER_ICON_COLOR }} />}
                      {text == 'Dashboard' && <StoreIcon style={{ color: HEADER_ICON_COLOR }} />}
                    </ListItemIcon>
                    <ListItemText style={{ color: "black" }} primary={text} />
                  </ListItemButton>
                </Link>
              )}
            </ListItem>
          ))}
        </List> */}

        <List>
          {menuList.map((text, index) => {
            // if (text === 'Designation' && role !== 'SUPERADMIN') return null;
            // Show "Designation" only to SUPERADMIN or VIEWER
            if (text === 'Designation' && !(role === 'SUPERADMIN' || role === 'VIEWER')) return null;
            return (
              <ListItem key={text} disablePadding>
                {text === 'Logout' ? (
                  <ListItemButton onClick={logout}>
                    <ListItemIcon>
                      <ExitToAppIcon style={{ color: HEADER_ICON_COLOR }} />
                    </ListItemIcon>
                    <ListItemText primary={text} />
                  </ListItemButton>
                ) : (
                  <Link
                    to={
                      text === 'Dashboard'
                        ? '/admin'
                        : text === 'Attendance Cycle'
                          ? '/admin/users/cycles'
                          : text === 'Quotation'
                            ? '/admin/quotation/create'
                            : text === 'Designation'
                              ? '/admin/user_types'
                              : text === 'Users'
                                ? '/admin/users'
                                : text === 'Reports'
                                  ? '/admin/reports'
                                  : text === 'Help'
                                    ? '/admin/help'
                                    : text === 'Purchase Order'
                                      ? '/admin/purchase_order_list'
                                      : '/admin'
                    }
                    className="linktag"
                  >
                    <ListItemButton>
                      <ListItemIcon>
                        {text === 'Dashboard' && <DashboardIcon style={{ color: HEADER_ICON_COLOR }} />}
                        {text === 'Attendance Cycle' && <StoreIcon style={{ color: HEADER_ICON_COLOR }} />}
                        {text === 'Quotation' && <DescriptionIcon style={{ color: HEADER_ICON_COLOR }} />}
                        {text === 'Designation' && <CorporateFareIcon style={{ color: HEADER_ICON_COLOR }} />}
                        {text === 'Users' && <GroupIcon style={{ color: HEADER_ICON_COLOR }} />}
                        {text === 'Reports' && <ListAltIcon style={{ color: HEADER_ICON_COLOR }} />}
                        {text === 'Help' && <ListAltIcon style={{ color: HEADER_ICON_COLOR }} />}
                        {text === 'Purchase Order' && <ListAltIcon style={{ color: HEADER_ICON_COLOR }} />}
                      </ListItemIcon>
                      <ListItemText style={{ color: "black" }} primary={text} />
                    </ListItemButton>
                  </Link>
                )}
              </ListItem>
            );
          })}
        </List>

        <Divider />

      </Drawer>
      <Main open={open}>
        <br /><br />
        {children}

      </Main>
    </Box>
  );
}
